import { BiConsumer, Consumer } from './../consumers/consumer';
import { arrayGenerator, Generator, objectGenerator } from './../generators/generator';
import { EqualsPredicate, isArray, isNumber, isPlainObject, isString } from './../predicates/predicates';

export const KEY_DEBUG = '$$DEBUG';

/*
 * Access the keys
 */
const _keys = Object.keys;

const _freeze = Object.freeze;
const _isFrozen = Object.isFrozen;

/*
 * Tests if two arrays are equal
 *
 * @param aLeft     left array
 * @param aRight    right array
 * @param aPredicate the equals predicate
 */
const _arrayEquals = <T>(aLeft: T[], aRight: T[], aPredicate: EqualsPredicate<T>): boolean => {
  // compare equality
  if (aLeft === aRight) {
    return true;
  }
  // check the type
  if (isArray(aLeft) && isArray(aRight)) {
    // test length
    let lLeft = aLeft.length;
    if (lLeft === aRight.length) {
      // check
      while (lLeft-- > 0) {
        if (!aPredicate(aLeft[lLeft], aRight[lLeft])) {
          return false;
        }
      }
      return true;
    }
  }
  // not the same
  return false;
}

/*
 * Tests if two objects are equal
 *
 * @param aLeft     left object
 * @param aRight    right object
 * @param aPredicate the equals predicate
 */
const _objectEquals = (aLeft: any, aRight: any, aPredicate: EqualsPredicate<any>): boolean => {
  // compare equality
  if (aLeft === aRight) {
    return true;
  }
  // check the type
  if (isPlainObject(aLeft) && isPlainObject(aRight)) {
    // compare the objects by key
    const leftKeys = _keys(aLeft);
    let lLeft = leftKeys.length;
    if (lLeft === _keys(aRight).length) {
      // check
      while (lLeft-- > 0) {
        const kLeft = leftKeys[lLeft];
        if ((kLeft !== KEY_DEBUG) && !aPredicate(aLeft[kLeft], aRight[kLeft])) {
          return false;
        }
      }
      // ok
      return true;
    }
  }
  // not equal
  return false;
}

/*
 * Tests if two objects are identical
 *
 * @param aLeft     left object
 * @param aRight    right object
 */
const _deepEquals = (aLeft: any, aRight: any): boolean => {
  // identity check
  return _arrayEquals(aLeft, aRight, _deepEquals) || _objectEquals(aLeft, aRight, _deepEquals);
}

/*
 * Pushes a value to an array
 *
 * @param aValue    the value
 * @param aArray    the array
 * @return the array
 */
const _push = <T>(aValue: T, aArray: T[]): T[] => {
  aArray.push(aValue);
  return aArray;
}

const _anyToString = (aValue: any): string => {
  return (aValue === undefined) ? 'undefined' : (aValue === null) ? 'null' : aValue.toString();
}

/*
 * Assigns a property to an object and returns that object
 *
 * @param aKey      key to assign
 * @param aValue    value to assign
 * @param aObject   the object
 * @return the object
 */
const _objectAssign = <T, V>(aKey: string, aValue: V, aObject: T): T => {
  aObject[aKey] = aValue;
  return aObject;
}

/*
 * Performs a deep clone for the value, supports just plain objects and arrays
 *
 * @param aValue
 */
function _cloneDeep(aValue: any): any {
  // clone as array
  if (isArray(aValue)) {
    // clone via map
    return aValue.map(_cloneDeep);
  }
  if (isPlainObject(aValue)) {
    // clone the object
    const result = {};
    // tslint:disable-next-line:forin
    for (const key in aValue) {
      result[key] = _cloneDeep(aValue[key]);
    }
    return result;
  }
  // nothing to clone
  return aValue;
}

const _assertFromGenerator = <T>(aKey: number | string | symbol, aObject: any, aGenerator: Generator<T>): T => aObject[aKey] || (aObject[aKey] = aGenerator());

const _assertObject = <T>(aKey: number | string | symbol, aObject: any): T => _assertFromGenerator<T>(aKey, aObject, objectGenerator);

const _assertArray = <T>(aKey: number | string | symbol, aObject: any): T[] => _assertFromGenerator<T[]>(aKey, aObject, arrayGenerator);

const _lastElement = <T>(aArray: T[]): T => aArray[aArray.length - 1];

/*
 * Converts the value to an integer
 *
 * @param aValue    the value
 * @param aDefault  the default value in case of an error
 */
function _toInteger(aValue: any, aDefault: number): number {
  // returns the number value directly
  if (isNumber(aValue)) {
    return aValue;
  }
  if (isString(aValue)) {
    try {
      return Number.parseInt(aValue, 10);
    } catch (err) {
      return aDefault;
    }
  }
  // else
  return aDefault;
}

const _UNDEFINED_TYPE = typeof undefined;
const _FUNCTION_TYPE = typeof _keys;


/*
 * Performs a deep freeze for the value, supports just plain objects and arrays
 *
 * @param aValue
 */
function _freezeDeep(aValue: any): any {
  // do not freeze frozen objects
  if (!_isFrozen(aValue)) {
    // clone as array
    if (isArray(aValue)) {
      const result = [];
      let idx = aValue.length;
      while (idx-- > 0) {
        result[idx] = _freezeDeep(aValue[idx]);
      }
      return _freeze(result);
    }
    if (isPlainObject(aValue)) {
      // clone the object
      const result = {};
      // tslint:disable-next-line:forin
      for (const key in aValue) {
        result[key] = _freezeDeep(aValue[key]);
      }
      return _freeze(result);
    }
  }
  // nothing to clone
  return aValue;
}

/*
 * Produces a random number within the bounds
 *
 * @param aMin  minimum bound
 * @param aMax  max bound
 * @return the result
 */
function _random(aMin: number, aMax: number): number {
  return aMin + Math.floor(Math.random() * (aMax - aMin));
}

/**
 * Constructs a getter description
 *
 * @param aGetter  the getter
 * @return the description of the getter
 */
function _createGetter<T>(aGetter: Generator<T>): PropertyDescriptor {
  // the descriptor
  return {
    'get': aGetter,
    'enumerable': true
  };
}

/**
 * Just run a callback for each element in an array
 *
 * @param aArray      the array
 * @param aConsumer   consumer callback
 */
function _forEach<T>(aArray: T[], aConsumer: Consumer<T>) {
  return aArray.forEach(aConsumer)
}

/**
 * Run a callback for each key value pair
 *
 * @param aObject     the object
 * @param aConsumer   consumer callback
 */
function _forIn<T>(aObject: { [key: string]: T }, aConsumer: BiConsumer<T, string>) {
  // make sure we have an object
  if (isPlainObject(aObject)) {
    // tslint:disable-next-line:forin
    for (const name in aObject) {
      // actual element
      aConsumer(aObject[name], name);
    }
  }
}

/**
 * Run a callback for each key value pair
 *
 * @param aObject     the object
 * @param aConsumer   consumer callback
 */
function _reduceForIn<T, R>(aObject: { [key: string]: T }, aFunction: (aRes: R, aValue: T, aKey: string) => R, aInitial: R): R {
  // the resulting object
  let result = aInitial;
  // make sure we have an object
  if (isPlainObject(aObject)) {
    // tslint:disable-next-line:forin
    for (const name in aObject) {
      // actual element
      result = aFunction(result, aObject[name], name);
    }
  }
  // ok
  return result;
}

export {
  _random as random,
  _arrayEquals as arrayEquals,
  _createGetter as createGetter,
  _objectEquals as objectEquals,
  _deepEquals as deepEquals,
  _freezeDeep as freezeDeep,
  _cloneDeep as cloneDeep,
  _push as arrayPush,
  _anyToString as anyToString,
  _assertArray as assertArray,
  _lastElement as lastElement,
  _assertObject as assertObject,
  _objectAssign as objectAssign,
  _toInteger as toInteger,
  _forEach as forEach,
  _forIn as forIn,
  _reduceForIn as reduceForIn,
  _UNDEFINED_TYPE as UNDEFINED_TYPE,
  _FUNCTION_TYPE as FUNCTION_TYPE
};
